import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  CloudSync as CalendarIcon,
} from '@mui/icons-material';
import calDAVService from '../services/calDAVService';
import type {
  CalDAVSettingDto,
  CreateCalDAVSettingRequest,
} from '../types/api';

const CalDAVSettings: React.FC = () => {
  const [settings, setSettings] = useState<CalDAVSettingDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<CreateCalDAVSettingRequest>({
    name: '',
    beschreibung: '',
    serverUrl: '',
    username: '',
    password: '',
    calendarPath: '',
    syncIntervalMinutes: 15,
    aktiv: true,
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [syncing, setSyncing] = useState<number | null>(null);
  const [syncLogs, setSyncLogs] = useState<any[]>([]);
  const [showLogs, setShowLogs] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await calDAVService.getAllSettings();
      setSettings(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Laden der CalDAV-Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (setting?: CalDAVSettingDto) => {
    if (setting) {
      setEditingId(setting.calDAVSettingID);
      setFormData({
        name: setting.name,
        beschreibung: setting.beschreibung || '',
        serverUrl: setting.serverUrl,
        username: setting.username,
        password: '', // Password nicht vorausfüllen
        calendarPath: setting.calendarPath,
        syncIntervalMinutes: setting.syncIntervalMinutes,
        aktiv: setting.aktiv,
      });
    } else {
      setEditingId(null);
      setFormData({
        name: '',
        beschreibung: '',
        serverUrl: '',
        username: '',
        password: '',
        calendarPath: '',
        syncIntervalMinutes: 15,
        aktiv: true,
      });
    }
    setTestResult(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setTestResult(null);
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setTestResult(null);
      const result = await calDAVService.testConnection(formData);
      setTestResult({
        success: result.success,
        message: result.message + (result.calendarName ? ` - Kalender: ${result.calendarName}` : ''),
      });
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.response?.data?.message || 'Verbindungstest fehlgeschlagen',
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingId) {
        await calDAVService.updateSetting(editingId, formData);
      } else {
        await calDAVService.createSetting(formData);
      }
      await loadSettings();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Möchten Sie diese CalDAV-Verbindung wirklich löschen?')) return;

    try {
      await calDAVService.deleteSetting(id);
      await loadSettings();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Löschen');
    }
  };

  const handleSync = async (id: number) => {
    try {
      setSyncing(id);
      const result = await calDAVService.syncAllTickets(id);
      setSuccess(`Synchronisation abgeschlossen: ${result.ticketsSynced} Tickets synchronisiert (${result.eventsCreated} erstellt, ${result.eventsUpdated} aktualisiert)`);
      await loadSettings();
      if (showLogs) {
        await loadSyncLogs();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler bei der Synchronisation');
    } finally {
      setSyncing(null);
    }
  };

  const loadSyncLogs = async (limit: number = 50) => {
    try {
      setLogsLoading(true);
      const logs = await calDAVService.getSyncLogs(undefined, limit);
      setSyncLogs(logs);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Laden der Sync-Logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleToggleLogs = () => {
    if (!showLogs) {
      loadSyncLogs();
    }
    setShowLogs(!showLogs);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">CalDAV Kalendersynchronisation</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CheckIcon />}
            onClick={handleToggleLogs}
          >
            {showLogs ? 'Logs ausblenden' : 'Sync-Logs anzeigen'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Neue Verbindung
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Server</TableCell>
              <TableCell>Benutzername</TableCell>
              <TableCell>Sync-Intervall</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Letzte Sync</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {settings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">
                    Keine CalDAV-Verbindungen konfiguriert
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              settings.map((setting) => (
                <TableRow key={setting.calDAVSettingID}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {setting.name}
                    </Typography>
                    {setting.beschreibung && (
                      <Typography variant="caption" color="textSecondary">
                        {setting.beschreibung}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {setting.serverUrl}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {setting.calendarPath}
                    </Typography>
                  </TableCell>
                  <TableCell>{setting.username}</TableCell>
                  <TableCell>{setting.syncIntervalMinutes} Min</TableCell>
                  <TableCell>
                    <Chip
                      label={setting.aktiv ? 'Aktiv' : 'Inaktiv'}
                      color={setting.aktiv ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {setting.lastSyncAt
                      ? new Date(setting.lastSyncAt).toLocaleString('de-DE')
                      : 'Noch nie'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Jetzt synchronisieren">
                      <IconButton
                        size="small"
                        onClick={() => handleSync(setting.calDAVSettingID)}
                        disabled={syncing === setting.calDAVSettingID || !setting.aktiv}
                      >
                        {syncing === setting.calDAVSettingID ? (
                          <CircularProgress size={20} />
                        ) : (
                          <SyncIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Bearbeiten">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(setting)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Löschen">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(setting.calDAVSettingID)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Sync-Logs Tabelle */}
      {showLogs && (
        <Paper sx={{ mt: 3, p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Synchronisations-Verlauf</Typography>
            <Button
              size="small"
              startIcon={<SyncIcon />}
              onClick={() => loadSyncLogs()}
              disabled={logsLoading}
            >
              Aktualisieren
            </Button>
          </Box>
          {logsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : syncLogs.length === 0 ? (
            <Alert severity="info">Noch keine Synchronisationen durchgeführt</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Zeitpunkt</TableCell>
                    <TableCell>Ticket</TableCell>
                    <TableCell>Richtung</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Event UID</TableCell>
                    <TableCell>Fehler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {syncLogs.map((log) => (
                    <TableRow key={log.logID}>
                      <TableCell>
                        {new Date(log.syncedAt).toLocaleString('de-DE')}
                      </TableCell>
                      <TableCell>
                        {log.ticketTitel || `Ticket #${log.ticketID}`}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.syncDirection}
                          size="small"
                          color={log.syncDirection === 'ToCalDAV' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.status}
                          size="small"
                          color={log.status === 'Success' ? 'success' : 'error'}
                          icon={log.status === 'Success' ? <CheckIcon /> : <ErrorIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" noWrap sx={{ maxWidth: 150, display: 'block' }}>
                          {log.eventUID || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {log.errorMessage && (
                          <Tooltip title={log.errorMessage}>
                            <Typography variant="caption" color="error" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                              {log.errorMessage}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Dialog für Erstellen/Bearbeiten */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'CalDAV-Verbindung bearbeiten' : 'Neue CalDAV-Verbindung'}
        </DialogTitle>
        <DialogContent>
          <form autoComplete="on">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
                autoComplete="name"
              />
              <TextField
                label="Beschreibung"
                value={formData.beschreibung}
                onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
              <TextField
                label="Server URL"
                value={formData.serverUrl}
                onChange={(e) => setFormData({ ...formData, serverUrl: e.target.value })}
                required
                fullWidth
                placeholder="https://nc.skeenanet.ch"
                helperText="Die Basis-URL deines Nextcloud-Servers"
                autoComplete="url"
              />
              <TextField
                label="Kalender-Pfad"
                value={formData.calendarPath}
                onChange={(e) => setFormData({ ...formData, calendarPath: e.target.value })}
                required
                fullWidth
                placeholder="/remote.php/dav/calendars/benutzername/skeena-termine/"
                helperText="Pfad zum Kalender (Nextcloud: /remote.php/dav/calendars/BENUTZER/KALENDER/)"
              />
              <TextField
                label="Benutzername"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                fullWidth
                autoComplete="username"
              />
              <TextField
                label="Passwort"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingId}
                fullWidth
                helperText={editingId ? 'Leer lassen, um Passwort nicht zu ändern' : 'Bei Nextcloud: App-Passwort empfohlen'}
                autoComplete="current-password"
              />
              <TextField
                label="Sync-Intervall (Minuten)"
                type="number"
                value={formData.syncIntervalMinutes}
                onChange={(e) =>
                  setFormData({ ...formData, syncIntervalMinutes: parseInt(e.target.value) || 15 })
                }
                required
                fullWidth
                inputProps={{ min: 5 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.aktiv}
                    onChange={(e) => setFormData({ ...formData, aktiv: e.target.checked })}
                  />
                }
                label="Aktiv"
              />

              {testResult && (
                <Alert severity={testResult.success ? 'success' : 'error'}>
                  {testResult.message}
                </Alert>
              )}
            </Box>
          </form>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleTestConnection}
            disabled={testingConnection || !formData.serverUrl || !formData.username}
            startIcon={testingConnection ? <CircularProgress size={20} /> : <CalendarIcon />}
          >
            Verbindung testen
          </Button>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || !formData.serverUrl || !formData.username}
          >
            {editingId ? 'Speichern' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalDAVSettings;
